import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Heart, Users, ClipboardList, Settings, BarChart3, Check, Mail, Phone, User, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import logoHorizontal from "@/assets/logo-horizontal.png";
import icon from "@/assets/icon.png";
import heroImage from "@/assets/hero-image.jpg";
import audienceProfessional from "@/assets/audience-professional.jpg";
import audienceParents from "@/assets/audience-parents.jpg";
import audienceElderly from "@/assets/audience-elderly.jpg";
import benefitsImage from "@/assets/benefits-image.jpg";
import blog1 from "@/assets/blog-1.jpg";
import blog2 from "@/assets/blog-2.jpg";
import blog3 from "@/assets/blog-3.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const Index = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    profile: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.profile) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const response = await supabase.functions.invoke("invite-user", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          profile_type: formData.profile,
        },
      });

      if (response.error || response.data?.error) {
        const msg = response.data?.error || response.error?.message || "Erro ao cadastrar";
        toast({ title: msg, variant: "destructive" });
        setLoading(false);
        return;
      }

      toast({
        title: "Cadastro realizado! 🎉",
        description: "Verifique seu e-mail para definir sua senha e acessar o MentalSync.",
      });
      setFormData({ name: "", email: "", phone: "", profile: "" });
    } catch (err: any) {
      toast({ title: "Erro ao cadastrar", description: err.message, variant: "destructive" });
    }
    setLoading(false);
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: "Sobre", id: "hero" },
    { label: "Para Quem", id: "audience" },
    { label: "Como Funciona", id: "how-it-works" },
    { label: "Blog", id: "blog" },
    { label: "Cadastre-se", id: "signup" },
  ];

  return (
    <div className="min-h-screen bg-background font-body">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <img src={logoHorizontal} alt="MentalSync" className="h-10" />
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Button
              variant="ghost"
              onClick={() => navigate("/auth")}
              className="text-foreground font-medium"
            >
              Entrar
            </Button>
            <Button
              onClick={() => scrollTo("signup")}
              className="bg-gold hover:bg-gold-dark text-accent-foreground font-semibold"
            >
              Quero Participar
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background border-b border-border py-4 px-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => scrollTo("signup")}
              className="mt-2 w-full bg-gold hover:bg-gold-dark text-accent-foreground font-semibold"
            >
              Quero Participar
            </Button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h1
              variants={fadeInUp}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground"
            >
              Cuide da{" "}
              <span className="text-gold">saúde mental</span>{" "}
              de quem você ama
            </motion.h1>
            <motion.p variants={fadeInUp} className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-lg">
              O MentalSync ajuda você a monitorar e acompanhar o bem-estar emocional de quem mais importa — com carinho, tecnologia e proximidade.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                onClick={() => scrollTo("signup")}
                size="lg"
                className="mt-8 bg-gold hover:bg-gold-dark text-accent-foreground font-semibold text-base px-8 py-6 shadow-lg"
              >
                Participe do Grupo de Teste
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img
              src={heroImage}
              alt="Pessoa em momento de paz e bem-estar em um jardim verde"
              className="rounded-2xl shadow-2xl w-full object-cover aspect-[16/10]"
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      {/* Audience Section */}
      <section id="audience" className="py-16 md:py-24 bg-jade-light">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl font-bold text-foreground">
              Para quem é o <span className="text-gold">MentalSync</span>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Desenvolvido para quem cuida e se preocupa com a saúde mental — a sua ou de alguém especial.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Brain,
                image: audienceProfessional,
                title: "Profissionais sob Estresse",
                description: "Você trabalha em ambientes de alta pressão? Monitore seu bem-estar emocional e previna o burnout com acompanhamento inteligente.",
              },
              {
                icon: Heart,
                image: audienceParents,
                title: "Pais e Tutores",
                description: "Acompanhe de perto crianças e dependentes com necessidades especiais — ansiedade, autismo, crises explosivas — com relatórios e alertas personalizados.",
              },
              {
                icon: Users,
                image: audienceElderly,
                title: "Cuidadores de Idosos",
                description: "Enfermeiros, filhos e familiares que cuidam de pessoas idosas. Tenha visibilidade do estado emocional e cognitivo com monitoramento contínuo.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-border"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-jade-light">
                      <item.icon className="h-6 w-6 text-gold" />
                    </div>
                    <h3 className="font-display text-xl font-semibold text-foreground">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl font-bold text-foreground">
              Como <span className="text-gold">Funciona</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Em três passos simples, comece a cuidar da saúde mental de forma inteligente.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
          >
            {[
              {
                icon: ClipboardList,
                step: "01",
                title: "Cadastre-se",
                description: "Preencha o formulário e entre para o grupo de teste gratuito do MentalSync.",
              },
              {
                icon: Settings,
                step: "02",
                title: "Configure",
                description: "Personalize o monitoramento de acordo com o perfil e as necessidades de quem será acompanhado.",
              },
              {
                icon: BarChart3,
                step: "03",
                title: "Acompanhe",
                description: "Receba relatórios, alertas e insights sobre o bem-estar emocional em tempo real.",
              },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeInUp} className="text-center">
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-jade-light flex items-center justify-center">
                    <item.icon className="h-8 w-8 text-gold" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gold text-accent-foreground text-sm font-bold flex items-center justify-center">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-jade-light">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src={benefitsImage}
              alt="Natureza e tranquilidade representando bem-estar mental"
              className="rounded-2xl shadow-xl w-full object-cover aspect-square"
              loading="lazy"
            />
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8">
              Por que escolher o <span className="text-gold">MentalSync</span>?
            </motion.h2>

            {[
              "Monitoramento contínuo e personalizado",
              "Alertas inteligentes para momentos críticos",
              "Relatórios claros e acessíveis",
              "Privacidade e segurança dos dados",
              "Suporte para múltiplos perfis de acompanhamento",
              "Interface simples e acolhedora",
            ].map((benefit, i) => (
              <motion.div key={i} variants={fadeInUp} className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                  <Check className="h-4 w-4 text-accent-foreground" />
                </div>
                <span className="text-foreground font-medium">{benefit}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Signup Form */}
      <section id="signup" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 max-w-2xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="rounded-2xl border-2 border-gold/30 bg-card p-8 md:p-12 shadow-xl"
          >
            <motion.div variants={fadeInUp} className="text-center mb-8">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
                Participe do <span className="text-gold">Grupo de Teste</span>
              </h2>
              <p className="mt-3 text-muted-foreground">
                Cadastre-se gratuitamente e seja um dos primeiros a experimentar o MentalSync.
              </p>
            </motion.div>

            <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-foreground font-medium">
                  Nome completo *
                </Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="Seu nome completo"
                    className="pl-10"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-foreground font-medium">
                  E-mail *
                </Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    maxLength={255}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-foreground font-medium">
                  Telefone
                </Label>
                <div className="relative mt-1.5">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(00) 00000-0000"
                    className="pl-10"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    maxLength={20}
                  />
                </div>
              </div>

              <div>
                <Label className="text-foreground font-medium">Perfil *</Label>
                <Select
                  value={formData.profile}
                  onValueChange={(value) => setFormData({ ...formData, profile: value })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Selecione seu perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profissional">Profissional sob estresse</SelectItem>
                    <SelectItem value="pai-tutor">Pai / Tutor de dependente</SelectItem>
                    <SelectItem value="cuidador-idoso">Cuidador de idoso</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold hover:bg-gold-dark text-accent-foreground font-semibold text-base py-6 shadow-lg"
              >
                {loading ? "Cadastrando..." : "Quero Participar"}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                Seus dados estão seguros. Não compartilhamos suas informações com terceiros.
              </p>
            </motion.form>
          </motion.div>
        </div>
      </section>

      {/* Blog */}
      <section id="blog" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl font-bold text-foreground">
              <span className="text-gold">Blog</span> — Cuidando da Mente
            </motion.h2>
            <motion.p variants={fadeInUp} className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Artigos e dicas para promover o bem-estar emocional no dia a dia.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                image: blog1,
                title: "5 hábitos matinais para começar o dia com mais calma",
                summary: "Pequenas mudanças na sua rotina podem transformar completamente a forma como você lida com o estresse diário.",
                date: "28 Fev 2026",
              },
              {
                image: blog2,
                title: "Como identificar sinais de ansiedade em crianças",
                summary: "Aprenda a reconhecer os sinais sutis que indicam que uma criança pode estar passando por dificuldades emocionais.",
                date: "20 Fev 2026",
              },
              {
                image: blog3,
                title: "O papel do cuidador na saúde mental do idoso",
                summary: "Descubra como o cuidador pode ser fundamental no acompanhamento e na manutenção da saúde emocional de pessoas idosas.",
                date: "15 Fev 2026",
              },
            ].map((post, i) => (
              <motion.article
                key={i}
                variants={fadeInUp}
                className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-border cursor-pointer group"
              >
                <div className="overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-medium text-gold">{post.date}</span>
                  <h3 className="font-display text-lg font-semibold text-foreground mt-2 mb-2 group-hover:text-gold transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{post.summary}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div>
              <img src={icon} alt="MentalSync" className="h-12 mb-4" />
              <p className="text-sm text-jade-medium">
                Monitoramento inteligente de saúde mental para quem cuida e para quem é cuidado.
              </p>
            </div>

            <div>
              <h4 className="font-display text-lg font-semibold text-background mb-4">Navegação</h4>
              <div className="space-y-2">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollTo(link.id)}
                    className="block text-sm text-jade-medium hover:text-gold transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-display text-lg font-semibold text-background mb-4">Contato</h4>
              <p className="text-sm text-jade-medium">contato@mentalsync.com.br</p>
              <div className="flex gap-4 mt-4">
                <a href="#" className="text-jade-medium hover:text-gold transition-colors" aria-label="Instagram">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="#" className="text-jade-medium hover:text-gold transition-colors" aria-label="LinkedIn">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-jade-medium/20 text-center">
            <p className="text-sm text-jade-medium">
              © 2026 MentalSync. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
